import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { NotFoundView, DefaultRouteError } from "@/components/RouteFallbacks";
import { useEffect } from "react";
import { Heart } from "lucide-react";
import { PAL_BY_SLUG, PALS, ELEMENT_COLORS, typeMultiplier, ELEMENTS, type Pal } from "@/lib/pals-data";
import { PalPortrait } from "@/components/PalPortrait";

import { getMountInfo, getBreedingParents, getVariants } from "@/lib/pals-extras";
import { useFavorites, useHistory } from "@/lib/user-data";
import { ShareButton } from "@/components/ShareButton";
import { GUIDE_BY_SLUG, GUIDES } from "@/lib/guides-data";
import { CardImage } from "@/components/CardImage";
import { palImageAbsolute } from "@/lib/pal-images";
import { FaqSection, faqSchema } from "@/components/PageFaq";
import { PageExpansion } from "@/components/PageExpansion";
import { SiloGateway, AnchorLink } from "@/components/SiloGateway";
import { palFaq } from "@/lib/pal-faq";
import { OFFICIAL_CARD_BY_CODE, normalizeCardCode } from "@/lib/tcg-cards";
import { resolveRetiredSlug, retiredSectionIndex } from "@/lib/seo-redirects";

export const Route = createFileRoute("/pals/$slug")({
  // Unknown Pal slugs redirect to the Pal database instead of a soft-404 page.
  beforeLoad: ({ params, location }) => {
    if (!PAL_BY_SLUG[params.slug]) {
      // Retired fan-variant Pal slugs ("neo-rooby", "lamball-solar") get one 301
      // to the official Pal they were derived from; the rest go to the Pal
      // database so no retired URL answers 404.
      const base = resolveRetiredSlug(params.slug, (s) => !!PAL_BY_SLUG[s]);
      throw redirect({ href: base ? `/pals/${base}` : retiredSectionIndex("pals"), statusCode: 301 });
    }
    const rawSearch = String((location as { searchStr?: string }).searchStr ?? "");
    const rawHref = String((location as { href?: string }).href ?? "");
    const raw = rawSearch || rawHref.slice(rawHref.indexOf("?") + 1);
    const paramsOnly = new URLSearchParams(raw.replace(/^\?/, ""));
    const hasEmptyLegacyFilters = ["q", "element", "rarity"].some((key) => paramsOnly.get(key) === "");
    if (hasEmptyLegacyFilters) {
      throw redirect({ to: "/pals/$slug", params: { slug: params.slug }, replace: true });
    }
  },
  loader: ({ params }): { pal: Pal } => {
    const pal = PAL_BY_SLUG[params.slug];
    if (!pal) throw notFound();
    return { pal };
  },

  head: ({ loaderData }) => {
    if (!loaderData) return { meta: [{ title: "Pal not found — Pal Stars" }, { name: "robots", content: "noindex" }] };
    const p = loaderData.pal;
    // "<name> palworld" is how these pages are actually searched, so the query
    // words sit at the front of the title and the description names the intents
    // (spawns, breeding, weakness) the page answers below the fold.
    const title = `${p.name} Palworld: Stats, Spawns & Breeding`;
    const base = `${p.name} Palworld guide (#${p.id}) — ${p.element} ${p.rarity} Pal stats, spawn locations, capture rate, breeding parents, moves, drops and weaknesses.`;
    const desc = base.length <= 155 ? base : base.slice(0, 152).replace(/\s+\S*$/, "") + "…";
    const url = `https://palstars.com/pals/${p.slug}`;
    const img = (p.tcg?.official && p.tcg.imageUrl) || palImageAbsolute(p.slug, 600);
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
        ...(img
          ? [
              { property: "og:image", content: img },
              { name: "twitter:image", content: img },
            ]
          : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        faqSchema(palFaq(p), url),
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: `${p.name} — ${p.element} ${p.rarity} Pal`,
            description: desc,
            mainEntityOfPage: url,
            ...(img ? { image: [img] } : {}),
            author: { "@type": "Organization", name: "Pal Stars", url: "https://palstars.com/", logo: "https://palstars.com/eidrolon-avatar.png" },
            publisher: { "@type": "Organization", name: "Pal Stars", url: "https://palstars.com/", logo: "https://palstars.com/eidrolon-avatar.png" },
            keywords: [p.name, p.element, p.rarity, "Palworld", "Pal"].join(", "),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: { "@id": "https://palstars.com/", name: "Home" } },
              { "@type": "ListItem", position: 2, name: "Pals", item: { "@id": "https://palstars.com/pals", name: "Pals" } },
              { "@type": "ListItem", position: 3, name: p.name, item: { "@id": url, name: p.name } },
            ],
          }),
        },
      ],
    };
  },
  component: PalDetail,
  errorComponent: DefaultRouteError,
  notFoundComponent: () => (
    <NotFoundView
      title="That Pal isn't in our database"
      message="No Palworld Pal matches this URL. Browse the full Pal database to find the Pal, its stats, moves and card number."
    />
  ),
});

function Stat({ label, value, max = 700 }: { label: string; value: number; max?: number }) {
  const pct = Math.min(100, (value / max) * 100);
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-semibold">{value}</span>
      </div>
      <div className="h-2 rounded-full bg-background/60 overflow-hidden">
        <div className="h-full rounded-full bg-gradient-to-r from-primary to-secondary" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function PalDetail() {
  const { pal } = Route.useLoaderData() as { pal: Pal };
  const color = ELEMENT_COLORS[pal.element];
  const idx = PALS.findIndex((p) => p.id === pal.id);
  const prev = PALS[(idx - 1 + PALS.length) % PALS.length];
  const next = PALS[(idx + 1) % PALS.length];
  const mount = getMountInfo(pal);
  const breedingParents = getBreedingParents(pal, 6);
  const variants = getVariants(pal);
  const { isFavorite, toggleFavorite } = useFavorites();
  const { pushHistory } = useHistory();
  useEffect(() => { pushHistory(pal.slug); }, [pal.slug, pushHistory]);
  const fav = isFavorite(pal.slug);

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
      <nav className="text-xs text-muted-foreground mb-6">
        <Link to="/" className="hover:text-foreground">Home</Link> / <Link to="/pals" className="hover:text-foreground">Pals</Link> / <span className="text-foreground">{pal.name}</span>
      </nav>

      <div className="grid gap-8 md:grid-cols-[280px_1fr]">
        <div className="glass rounded-2xl p-4">
          {pal.tcg?.official && pal.tcg.imageUrl ? (
            <CardImage
              src={pal.tcg.imageUrl}
              alt={`${pal.name} Palworld TCG card ${pal.tcg.cardNumber} (${pal.tcg.rarityCode})`}
              width={260}
              height={363}
              priority
              responsive
              className="w-full rounded-xl aspect-[5/7]"
            />
          ) : (
            <PalPortrait pal={pal} size={260} />
          )}
          <div className="mt-4 flex items-center justify-between">
            <div>
              <div className="font-mono text-xs text-muted-foreground">{pal.tcg.cardNumber}</div>
              <div className="font-semibold">{pal.rarity}</div>
            </div>
            <span className="text-xs px-2 py-1 rounded-full" style={{ background: color + "22", color }}>
              {pal.element}{pal.secondary ? ` / ${pal.secondary}` : ""}
            </span>
          </div>
          <div className="mt-4 flex justify-between text-xs">
            <Link to="/pals/$slug" params={{ slug: prev.slug }} className="text-primary hover:underline">← {prev.name}</Link>
            <Link to="/pals/$slug" params={{ slug: next.slug }} className="text-primary hover:underline">{next.name} →</Link>
          </div>
          <a
            href={`${pal.tcg.sourceUrl}?q=${encodeURIComponent(pal.name)}`}
            target="_blank"
            rel="external nofollow noopener noreferrer"
            className="mt-3 block text-center text-[11px] text-muted-foreground hover:text-primary"
          >
            View {pal.name} on the Palworld Official Card Game ↗
          </a>
          {(() => {
            const key = pal.slug.toLowerCase();
            const exact = GUIDE_BY_SLUG[key];
            const related = exact ?? GUIDES.find((g) => g.slug.startsWith(key + "-") || g.slug.endsWith("-" + key) || g.keywords.toLowerCase().includes(pal.name.toLowerCase()));
            if (!related) return null;
            return (
              <Link
                to="/guides/$slug"
                params={{ slug: related.slug }}
                className="mt-2 block text-center text-[11px] font-semibold text-primary hover:underline"
              >
                Read the {pal.name} guide →
              </Link>
            );
          })()}
        </div>

        <div>
          <div className="flex items-start justify-between gap-4">
            <h1 className="text-4xl font-black">{pal.name}</h1>
            <div className="flex items-center gap-2">
              <ShareButton title={`${pal.name} — Pal Stars`} text={pal.description} path={`/pals/${pal.slug}`} />
              <button onClick={() => toggleFavorite(pal.slug)}
                className={`rounded-full px-3 py-2 text-xs flex items-center gap-1.5 border ${fav ? "bg-destructive/15 border-destructive text-destructive" : "hover:bg-card"}`}>
                <Heart className={`h-4 w-4 ${fav ? "fill-current" : ""}`} />
                {fav ? "Favorited" : "Favorite"}
              </button>
            </div>
          </div>
          <p className="mt-3 text-muted-foreground max-w-2xl">{pal.description}</p>

          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <div className="glass rounded-xl p-4 space-y-3">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Base stats</h2>
              <Stat label="HP" value={pal.hp} max={800} />
              <Stat label="Attack" value={pal.attack} max={400} />
              <Stat label="Defense" value={pal.defense} max={400} />
              <Stat label="Speed" value={pal.speed} max={200} />
              <Stat label="Stamina" value={pal.stamina} max={400} />
              <Stat label="Ride speed" value={pal.rideSpeed} max={1500} />
              <Stat label="Work speed" value={pal.workSpeed} max={300} />
              <div className="grid grid-cols-2 gap-2 pt-2 border-t mt-2">
                <div className="rounded-lg bg-background/40 border p-2">
                  <div className="text-[10px] uppercase text-muted-foreground">Capture rate</div>
                  <div className="text-lg font-bold">{pal.captureRate}%</div>
                </div>
                <div className="rounded-lg bg-background/40 border p-2">
                  <div className="text-[10px] uppercase text-muted-foreground">Food / day</div>
                  <div className="text-lg font-bold">{pal.foodRequirement} slots</div>
                </div>
              </div>
              <Link to="/capture-rates" className="block text-xs text-primary hover:underline">See full capture rate table →</Link>
            </div>
            <div className="glass rounded-xl p-4">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">Ratings</h2>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <RatingBar label="PvE" value={pal.pveRating} />
                <RatingBar label="PvP" value={pal.pvpRating} />
                <RatingBar label="Base" value={pal.baseRating} />
                <RatingBar label="Mount" value={pal.mountRating} />
              </div>
              <h3 className="mt-5 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Partner skill</h3>
              <p className="mt-1 font-semibold">{pal.partnerSkill}</p>
              <h3 className="mt-4 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Work suitability</h3>
              <div className="mt-1 flex flex-wrap gap-2">
                {pal.work.map((w) => <span key={w} className="text-xs px-2 py-1 rounded-full bg-card border">{w}</span>)}
              </div>
              <h3 className="mt-4 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Drops</h3>
              <div className="mt-1 flex flex-wrap gap-2">
                {pal.drops.map((d) => <span key={d} className="text-xs px-2 py-1 rounded-full bg-card border">{d}</span>)}
              </div>
              <h3 className="mt-4 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Spawn</h3>
              <p className="mt-1 text-sm">{pal.spawn}</p>
            </div>
          </div>

          <section className="glass rounded-xl p-4 mt-6">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
              <div>
                <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Official Card Game data</h2>
              </div>
              <AnchorLink to="/cards">
                Open the Palworld TCG Card List →
              </AnchorLink>
            </div>
            {/* Only real printed cards own a /cards/<code> page. Many Pals carry a
                placeholder PSD-* number, and linking those produced 404s. */}
            {OFFICIAL_CARD_BY_CODE.get(normalizeCardCode(pal.tcg.cardNumber)) && (
              <SiloGateway mode="game" name={pal.name} target={pal.tcg.cardNumber} className="mb-4" />
            )}
            <div className="grid gap-4 lg:grid-cols-[220px_1fr]">
              <div className="rounded-xl border bg-background/40 p-3">
                {pal.tcg.imageUrl ? (
                  <CardImage
                    src={pal.tcg.imageUrl}
                    alt={`${pal.name} Palworld Official Card Game card ${pal.tcg.cardNumber}`}
                    width={200}
                    height={280}
                    responsive
                    className="w-full rounded-xl aspect-[5/7]"
                  />
                ) : (
                  <div className="aspect-[5/7] rounded-lg bg-card p-3 flex flex-col justify-between">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="text-[10px] text-muted-foreground">{pal.tcg.cardNumber}</div>
                        <div className="font-display text-lg font-bold leading-tight">{pal.name}</div>
                      </div>
                      <span className="rounded-full border px-2 py-0.5 text-[10px]">{pal.tcg.color}</span>
                    </div>
                    <PalPortrait pal={pal} size={160} />
                    <div className="space-y-1 text-xs">
                      <div className="font-semibold">{pal.tcg.cardTitle}</div>
                      <p className="text-muted-foreground line-clamp-3">{pal.tcg.cardText}</p>
                    </div>
                  </div>
                )}
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {[
                  ["Card no.", pal.tcg.cardNumber],
                  ["Set", pal.tcg.set],
                  ["Type", pal.tcg.cardType],
                  ["Color", pal.tcg.color],
                  ["Cost", pal.tcg.cost],
                  ["Power", pal.tcg.power],
                  ["Strike", pal.tcg.strike],
                  ["Rarity", pal.tcg.rarityCode],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-lg border bg-background/40 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
                    <div className="mt-1 font-semibold">{value}</div>
                  </div>
                ))}
                <div className="rounded-lg border bg-background/40 p-3 sm:col-span-2">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Keywords / work icons</div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {[...pal.tcg.keywords, ...pal.tcg.workIcons].map((tag) => <span key={tag} className="rounded-full bg-card px-2 py-1 text-xs border">{tag}</span>)}
                  </div>
                </div>
                <div className="rounded-lg border bg-background/40 p-3 sm:col-span-2">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Card text</div>
                  <p className="mt-1 text-sm">{pal.tcg.cardText}</p>
                  <p className="mt-2 text-xs text-muted-foreground">{pal.tcg.flavorText}</p>
                  
                </div>
              </div>
            </div>
          </section>

          <div className="glass rounded-xl p-4 mt-6">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4">Moveset</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              {pal.moves.map((m, i) => (
                <div key={i} className="rounded-lg border p-3 bg-background/40">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold">{m.name}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: ELEMENT_COLORS[m.element] + "22", color: ELEMENT_COLORS[m.element] }}>
                      {m.element}
                    </span>
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground flex gap-4">
                    <span>Power <b className="text-foreground">{m.power}</b></span>
                    <span>Cooldown <b className="text-foreground">{m.cooldown}s</b></span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="glass rounded-xl p-4 mt-6">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4">Skill tree</h2>
            <div className="space-y-4">
              {[1, 2, 3].map((tier) => {
                const nodes = pal.skillTree.filter((s) => s.tier === tier);
                return (
                  <div key={tier}>
                    <div className="text-[10px] uppercase tracking-widest text-primary mb-2">Tier {tier}</div>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {nodes.map((n) => (
                        <div key={n.name} className="rounded-lg border p-3 bg-background/40">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-sm">{n.name}</span>
                            <span className="text-[10px] text-muted-foreground">Lv {n.unlockLevel} · {n.cost} pt</span>
                          </div>
                          <p className="mt-1 text-xs text-muted-foreground">{n.effect}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>


          <div className="glass rounded-xl p-4 mt-6">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">Type matchups</h2>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 text-xs">
              {ELEMENTS.map((def) => {
                const mult = typeMultiplier(pal.element, def);
                const tone = mult > 1 ? "text-success" : mult < 1 ? "text-destructive" : "text-muted-foreground";
                return (
                  <div key={def} className="rounded-md border p-2 bg-background/40 flex justify-between">
                    <span>{def}</span>
                    <span className={tone + " font-bold"}>×{mult}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 mt-6">
            <div className="glass rounded-xl p-4">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">Mount info</h2>
              {mount.canMount ? (
                <>
                  <div className="text-sm mb-3">Category: <b>{mount.category}</b></div>
                  <div className="grid grid-cols-3 gap-2 text-center text-xs">
                    <div className="rounded-lg border p-2 bg-background/40"><div className="text-muted-foreground">Fly</div><div className="font-bold text-base">{mount.flySpeed || "—"}</div></div>
                    <div className="rounded-lg border p-2 bg-background/40"><div className="text-muted-foreground">Ground</div><div className="font-bold text-base">{mount.groundSpeed || "—"}</div></div>
                    <div className="rounded-lg border p-2 bg-background/40"><div className="text-muted-foreground">Swim</div><div className="font-bold text-base">{mount.swimSpeed || "—"}</div></div>
                  </div>
                </>
              ) : <p className="text-sm text-muted-foreground">This Pal cannot be ridden.</p>}
            </div>

            <div className="glass rounded-xl p-4">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">Variants (incl. Eidrolon)</h2>
              <div className="space-y-2">
                {variants.map((v) => (
                  <div key={v.tag} className="rounded-lg border p-2 bg-background/40">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: v.color + "22", color: v.color }}>{v.tag}</span>
                      <span className="text-[10px] text-muted-foreground">HP ×{v.hpMult} · ATK ×{v.atkMult}</span>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">{v.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="glass rounded-xl p-4 mt-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Breeding — parent combos</h2>
              <Link to="/breeding" className="text-xs text-primary hover:underline">Open calculator →</Link>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              {breedingParents.map((pair, i) => (
                <div key={i} className="rounded-lg border p-2 bg-background/40 text-xs flex items-center gap-2">
                  <Link to="/pals/$slug" params={{ slug: pair.a.slug }} className="hover:underline font-medium">{pair.a.name}</Link>
                  <span className="text-muted-foreground">+</span>
                  <Link to="/pals/$slug" params={{ slug: pair.b.slug }} className="hover:underline font-medium">{pair.b.name}</Link>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <PageExpansion path={`/pals/${pal.slug}`} />

      <FaqSection items={palFaq(pal)} heading={`${pal.name} Palworld FAQ`} />

      <nav aria-label={`${pal.name} related Palworld tools`} className="glass rounded-2xl p-6 mt-8">
        <h2 className="text-lg font-bold">Plan your {pal.name} run</h2>
        <ul className="mt-3 grid gap-2 sm:grid-cols-2 text-sm">
          <li><Link to="/breeding" className="text-primary hover:underline">Palworld breeding calculator — parent combos for {pal.name}</Link></li>
          <li><Link to="/map" className="text-primary hover:underline">Palworld map — {pal.spawn} spawn and fast travel points</Link></li>
          <li><Link to="/guides/$slug" params={{ slug: "palworld-breeding-guide" }} className="text-primary hover:underline">Palworld breeding guide — cakes, breed power and eggs</Link></li>
          <li><Link to="/damage" search={{ boss: undefined }} className="text-primary hover:underline">Damage calculator — {pal.element} matchups and DPS</Link></li>
        </ul>
      </nav>
    </div>
  );
}

function RatingBar({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="flex justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-semibold">{value}/10</span>
      </div>
      <div className="mt-1 h-1.5 rounded-full bg-background/60 overflow-hidden">
        <div className="h-full bg-gradient-to-r from-primary to-secondary" style={{ width: `${value * 10}%` }} />
      </div>
    </div>
  );
}
